import type { DaemonTrigger, DaemonTriggerLog, DaemonTriggerLogCategory } from '../lib/api';
import type { JSONContent } from '@tiptap/core';
import { formatLocalDateTime } from '../lib/dateTime';

export const formatDaemonTriggerDate = (dateStr: string) => {
  return formatLocalDateTime(dateStr);
};

export const formatHeartbeatRelative = (
  dateStr: string,
  t: (key: string, values?: Record<string, number>) => string,
): string => {
  const diffSeconds = Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000));
  if (diffSeconds < 60) {
    return t('secondsAgo', { count: Math.max(diffSeconds, 1) });
  }
  if (diffSeconds < 3600) {
    return t('minutesAgo', { count: Math.floor(diffSeconds / 60) });
  }
  return t('hoursAgo', { count: Math.floor(diffSeconds / 3600) });
};

export const isTerminalStatus = (status: DaemonTrigger['status']) =>
  status === 'DONE' || status === 'CANCELLED' || status === 'FAILED' || status === 'REJECTED';

type ContinueOverrideSource = Pick<
  DaemonTrigger,
  'runnerType' | 'model' | 'fastMode' | 'effort' | 'planMode' | 'userConventionIds'
>;

export type ContinueOverrides = {
  runnerType?: string;
  model?: string | null;
  fastMode?: boolean;
  effort?: string | null;
  planMode?: boolean;
  userConventionIds?: string[];
};

export const buildDaemonTriggerContinueOverrides = ({
  source,
  continueRunnerType,
  continueModel,
  continueFastMode,
  continueEffort,
  effectiveContinuePlanMode,
  continueUserConventionIds,
}: {
  source: ContinueOverrideSource | null;
  continueRunnerType: string;
  continueModel: string;
  continueFastMode: boolean;
  continueEffort: string;
  effectiveContinuePlanMode: boolean;
  continueUserConventionIds: string[];
}): ContinueOverrides | undefined => {
  const overrides: ContinueOverrides = {};
  if (continueRunnerType && continueRunnerType !== source?.runnerType) {
    overrides.runnerType = continueRunnerType;
    overrides.model = continueModel || null;
    overrides.fastMode = continueFastMode;
  } else if (continueModel !== (source?.model ?? '')) {
    overrides.model = continueModel || null;
    overrides.fastMode = continueFastMode;
  } else if (continueFastMode !== (source?.fastMode ?? false)) {
    overrides.fastMode = continueFastMode;
  }

  if (overrides.model !== undefined) {
    overrides.effort = continueEffort || null;
  } else if (continueEffort !== (source?.effort ?? '')) {
    overrides.effort = continueEffort || null;
  }
  if (effectiveContinuePlanMode !== (source?.planMode ?? false)) {
    overrides.planMode = effectiveContinuePlanMode;
  }
  const sourceUserConventionIds = source?.userConventionIds ?? [];
  const sameUserConventions =
    sourceUserConventionIds.length === continueUserConventionIds.length &&
    sourceUserConventionIds.every((id) => continueUserConventionIds.includes(id));
  if (!sameUserConventions) {
    overrides.userConventionIds = continueUserConventionIds;
  }
  return Object.keys(overrides).length > 0 ? overrides : undefined;
};

export const formatDuration = (
  startedAt: string | null,
  endedAt: string,
  t: (key: string, values?: Record<string, number>) => string,
): string | null => {
  if (!startedAt) return null;
  const diffSeconds = Math.max(0, Math.floor((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000));
  if (diffSeconds < 60) {
    return t('durationSeconds', { count: Math.max(diffSeconds, 1) });
  }
  if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    const seconds = diffSeconds % 60;
    return seconds > 0 ? t('durationMinutesSeconds', { minutes, seconds }) : t('durationMinutes', { count: minutes });
  }
  const hours = Math.floor(diffSeconds / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  return minutes > 0 ? t('durationHoursMinutes', { hours, minutes }) : t('durationHours', { count: hours });
};

export const hasContent = (node: JSONContent | undefined): boolean => {
  return getPlainText(node).trim().length > 0;
};

export const appendPromptContent = (current: JSONContent | undefined, next: JSONContent): JSONContent => {
  if (!hasContent(current)) {
    return next;
  }
  if (!hasContent(next)) {
    return current as JSONContent;
  }

  return {
    type: 'doc',
    content: [...(current?.content ?? []), ...(next.content ?? [])],
  };
};

export const removeEntityReference = (
  prompt: JSONContent | undefined,
  target: { entityType: string; entityId: string },
): JSONContent | undefined => {
  if (!prompt?.content) return prompt;

  let removed = false;
  const content = prompt.content
    .map((node) => {
      if (!node.content) return node;
      const nextContent = node.content.filter((child) => {
        const matches =
          child.type === 'entityReference' &&
          child.attrs?.entityType === target.entityType &&
          child.attrs?.entityId === target.entityId;
        if (matches) removed = true;
        return !matches;
      });
      if (nextContent.length === node.content.length) return node;
      // 엔티티 칩 뒤에는 캐럿용 공백 텍스트 노드가 붙어 있다. 칩을 제거한 뒤
      // 남은 자식이 공백뿐이면 문단을 비워, 아래 filter에서 함께 제거되도록 한다.
      const isWhitespaceOnly = nextContent.every((child) => child.type === 'text' && (child.text ?? '').trim() === '');
      return { ...node, content: isWhitespaceOnly ? [] : nextContent };
    })
    .filter((node) => node.type !== 'paragraph' || (node.content?.length ?? 0) > 0);

  return removed ? { ...prompt, content } : prompt;
};

export const hasEntityReference = (
  prompt: JSONContent | undefined,
  target: { entityType: string; entityId: string },
): boolean =>
  prompt?.content?.some((node) =>
    node.content?.some(
      (child) =>
        child.type === 'entityReference' &&
        child.attrs?.entityType === target.entityType &&
        child.attrs?.entityId === target.entityId,
    ),
  ) ?? false;

export const getTextLength = (node: JSONContent | undefined): number => {
  return getPlainText(node).length;
};

export const getPlainText = (node: JSONContent | undefined): string => {
  if (!node) return '';
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'entityReference') return typeof node.attrs?.label === 'string' ? node.attrs.label : '';
  if (node.type === 'hardBreak') return '\n';
  if (node.content) {
    const separator = node.type === 'doc' ? '\n' : '';
    return node.content.map(getPlainText).join(separator);
  }
  return '';
};

const LEGACY_CODE_REVIEW_HITL_GUIDANCE =
  '낮은 위험도 리뷰 생략을 뜻하지 않습니다. 근거 부족은 미판정이며 실행·승인·머지 결정은 사람이 합니다.';
const LEGACY_CODE_REVIEW_RISK_PREFIX = '리뷰 강도 제안: ';

const hasCodeReviewReference = (node: JSONContent): boolean => {
  if (node.type === 'entityReference' && node.attrs?.entityType === 'codeReview') return true;
  return node.content?.some(hasCodeReviewReference) ?? false;
};

const isLegacyCodeReviewRiskParagraph = (node: JSONContent): boolean => {
  if (node.type !== 'paragraph') return false;
  const text = getPlainText(node).trim();
  if (!text.startsWith(LEGACY_CODE_REVIEW_RISK_PREFIX)) return false;

  try {
    const risk = JSON.parse(text.slice(LEGACY_CODE_REVIEW_RISK_PREFIX.length)) as Record<string, unknown>;
    return (
      typeof risk.ruleVersion === 'string' &&
      typeof risk.evaluatedAt === 'string' &&
      typeof risk.level === 'string' &&
      typeof risk.intensity === 'string' &&
      Array.isArray(risk.reasons)
    );
  } catch {
    return false;
  }
};

/**
 * 과거 코드리뷰 정책이 prompt에 덧붙인 평가 문단만 읽기 표면에서 숨긴다.
 * 저장 원문과 일반 러너 요청은 보존하기 위해 정책 id와 codeReview 참조가 둘 다 있을 때만 적용한다.
 */
export const sanitizeLegacyCodeReviewPrompt = (
  prompt: DaemonTrigger['prompt'],
  runnerDispatchPolicyId: string | null | undefined,
): DaemonTrigger['prompt'] => {
  if (!runnerDispatchPolicyId || typeof prompt !== 'object' || prompt === null) return prompt;

  const document = prompt as JSONContent;
  if (!document.content || !hasCodeReviewReference(document)) return prompt;

  const content = document.content.filter((node) => {
    const text = getPlainText(node).trim();
    return text !== LEGACY_CODE_REVIEW_HITL_GUIDANCE && !isLegacyCodeReviewRiskParagraph(node);
  });
  return content.length === document.content.length ? prompt : { ...document, content };
};

export type LogVisibilityMode = 'RESULT_ONLY' | 'WITH_TOOL' | 'ALL';

export const DEFAULT_LOG_VISIBILITY_MODE: LogVisibilityMode = 'WITH_TOOL';

const LOG_CATEGORIES_BY_VISIBILITY_MODE: Record<LogVisibilityMode, ReadonlySet<DaemonTriggerLogCategory>> = {
  RESULT_ONLY: new Set(['RESULT', 'TEXT', 'STDERR']),
  WITH_TOOL: new Set(['RESULT', 'TEXT', 'STDERR', 'TOOL', 'SYSTEM']),
  ALL: new Set(['SYSTEM', 'TEXT', 'THINKING', 'TOOL', 'RESULT', 'STDERR']),
};

const inferLegacyLogCategory = (log: DaemonTriggerLog): DaemonTriggerLogCategory | null => {
  const message = log.message.trim();
  if (/^\[(?:Tool|Patch)\]/i.test(message)) return 'TOOL';
  if (/^\[Result\]/i.test(message)) return 'RESULT';
  if (/^\[Thinking\]/i.test(message)) return 'THINKING';
  if (/^\[Session\]/i.test(message)) return 'SYSTEM';
  if (
    /^(?:Trigger started with runner|Runtime fetched|Session initialized|Command (?:started|succeeded|failed)|Tokens used:)/.test(
      message,
    )
  ) {
    return 'SYSTEM';
  }
  return null;
};

export const normalizeLegacyLogCategories = <T extends DaemonTriggerLog>(logs: readonly T[]): T[] =>
  logs.map((log) => {
    if (log.category != null) return log;
    const category = inferLegacyLogCategory(log);
    return category ? { ...log, category } : log;
  });

export const LOG_GROUP_THRESHOLD = 2;

export const filterLogsByMode = <T extends DaemonTriggerLog>(logs: readonly T[], mode: LogVisibilityMode): T[] => {
  const visibleCategories = LOG_CATEGORIES_BY_VISIBILITY_MODE[mode];
  return logs.filter((log) => log.category == null || visibleCategories.has(log.category));
};

export type GroupedDaemonTriggerLog<T extends DaemonTriggerLog = DaemonTriggerLog> =
  | {
      kind: 'single';
      log: T;
    }
  | {
      kind: 'group';
      category: T['category'];
      toolName: string;
      count: number;
      items: T[];
    };

export const groupAdjacentLogs = <T extends DaemonTriggerLog>(logs: readonly T[]): GroupedDaemonTriggerLog<T>[] => {
  const groupedLogs: GroupedDaemonTriggerLog<T>[] = [];
  let index = 0;

  while (index < logs.length) {
    const log = logs[index];
    if (!log) break;

    const toolName = log.toolName?.trim();
    if (!toolName) {
      groupedLogs.push({ kind: 'single', log });
      index += 1;
      continue;
    }

    const items = [log];
    let nextIndex = index + 1;
    while (nextIndex < logs.length) {
      const nextLog = logs[nextIndex];
      if (
        !nextLog ||
        nextLog.level !== log.level ||
        nextLog.category !== log.category ||
        nextLog.toolName !== log.toolName
      ) {
        break;
      }
      items.push(nextLog);
      nextIndex += 1;
    }

    if (items.length >= LOG_GROUP_THRESHOLD) {
      groupedLogs.push({
        kind: 'group',
        category: log.category,
        toolName,
        count: items.length,
        items,
      });
    } else {
      groupedLogs.push({ kind: 'single', log });
    }
    index = nextIndex;
  }

  return groupedLogs;
};

export const LOG_MESSAGE_PREVIEW_LINE_COUNT = 3;

export const getLogMessagePreview = (
  message: string,
  maxLines = LOG_MESSAGE_PREVIEW_LINE_COUNT,
): { text: string; isTruncated: boolean } => {
  const lines = message.split(/\r?\n/);
  return {
    text: lines.slice(0, maxLines).join('\n'),
    isTruncated: lines.length > maxLines,
  };
};

export type ToolLogPresentation = {
  toolName: string | null;
  target: string | null;
};

export const getToolLogPresentation = (log: Pick<DaemonTriggerLog, 'message' | 'toolName'>): ToolLogPresentation => {
  const body = log.message
    .trim()
    .replace(/^\[(?:Tool|Patch)\]\s*/i, '')
    .trim();
  const structuredToolName = log.toolName?.trim() || null;

  if (structuredToolName) {
    if (body === structuredToolName) {
      return { toolName: structuredToolName, target: null };
    }

    const target =
      body.startsWith(`${structuredToolName}:`) || body.startsWith(`${structuredToolName} `)
        ? body.slice(structuredToolName.length).replace(/^:\s*/, '').trim()
        : body;
    return { toolName: structuredToolName, target: target || null };
  }

  const colonIndex = body.indexOf(':');
  if (colonIndex > 0) {
    return {
      toolName: body.slice(0, colonIndex).trim() || null,
      target: body.slice(colonIndex + 1).trim() || null,
    };
  }

  const parentheticalTarget = body.match(/^(.+?)\s+(\([^)]*\))$/);
  if (parentheticalTarget) {
    return {
      toolName: parentheticalTarget[1]?.trim() || null,
      target: parentheticalTarget[2]?.trim() || null,
    };
  }

  return { toolName: body || null, target: null };
};

// eslint-disable-next-line no-control-regex -- ANSI 이스케이프 제거가 이 정규식의 목적이다.
const stripAnsi = (value: string): string => value.replace(/\u001B\[[0-9;?]*[ -/]*[@-~]/g, '');

const isHiddenCodexLine = (line: string): boolean => {
  const trimmed = line.trim();
  if (trimmed.length === 0) return true;
  if (trimmed === 'user' || trimmed === '--------') return true;
  if (trimmed.startsWith('<details>') || trimmed.startsWith('</details>')) return true;
  if (trimmed.startsWith('<summary>') || trimmed.startsWith('</summary>')) return true;
  if (trimmed.startsWith('**[') || trimmed.startsWith('--- BEGIN:') || trimmed.startsWith('--- END')) return true;
  if (trimmed.startsWith('History context') || trimmed.startsWith('- History path:')) return true;
  if (/^(workdir|model|provider|approval|sandbox|reasoning effort|reasoning summaries|session id):/.test(trimmed))
    return true;
  if (/^(diff --git|index |@@ |--- |\+\+\+ )/.test(trimmed)) return true;
  if (/^[+-]/.test(trimmed)) return true;
  if (/^[{}[\]",]+$/.test(trimmed)) return true;
  if (/^(const|let|function|type|export|import|class|return|if|for|while)\b/.test(trimmed)) return true;
  if (/^[.#]?[A-Za-z0-9_-]+\s*\{?$/.test(trimmed)) return true;
  return false;
};

const extractCodexCommand = (line: string): string => {
  const doubleQuoted = line.match(/-lc "(.+)" in /);
  if (doubleQuoted?.[1]) return doubleQuoted[1];
  const singleQuoted = line.match(/-lc '(.+)' in /);
  if (singleQuoted?.[1]) return singleQuoted[1];
  return line.replace(/\s+in\s+\/.+$/, '').trim();
};

const toCodexReadableMessage = (message: string): Array<{ level: DaemonTriggerLog['level']; message: string }> => {
  const lines = stripAnsi(message).split(/\r?\n/);
  const items: Array<{ level: DaemonTriggerLog['level']; message: string }> = [];
  let isAfterCodexStart = false;
  let nextLineKind: 'codex' | 'command' | 'usage' | null = null;
  let isInsideCommandOutput = false;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line.length === 0) continue;

    if (line === 'codex') {
      isAfterCodexStart = true;
      nextLineKind = 'codex';
      isInsideCommandOutput = false;
      continue;
    }

    if (!isAfterCodexStart) {
      continue;
    }

    if (line === 'exec') {
      nextLineKind = 'command';
      isInsideCommandOutput = false;
      continue;
    }

    if (line === 'tokens used') {
      nextLineKind = 'usage';
      isInsideCommandOutput = false;
      continue;
    }

    if (nextLineKind === 'codex') {
      if (!isHiddenCodexLine(line)) {
        items.push({ level: 'INFO', message: line });
      }
      nextLineKind = null;
      continue;
    }

    if (nextLineKind === 'command') {
      if (!isHiddenCodexLine(line)) {
        items.push({ level: 'INFO', message: `Command started: ${extractCodexCommand(line)}` });
      }
      nextLineKind = null;
      continue;
    }

    if (nextLineKind === 'usage') {
      if (/^[0-9,]+$/.test(line)) {
        items.push({ level: 'INFO', message: `Tokens used: ${line}` });
      }
      nextLineKind = null;
      continue;
    }

    if (/^succeeded in \d+ms:/.test(line)) {
      items.push({ level: 'INFO', message: 'Command succeeded' });
      isInsideCommandOutput = true;
      continue;
    }

    if (/^failed in \d+ms:/.test(line)) {
      items.push({ level: 'ERROR', message: 'Command failed' });
      isInsideCommandOutput = true;
      continue;
    }

    if (isInsideCommandOutput) {
      continue;
    }

    if (/^\d{4}-\d{2}-\d{2}T.*\bERROR\b/.test(line)) {
      items.push({ level: 'ERROR', message: line.replace(/^\d{4}-\d{2}-\d{2}T\S+\s+ERROR\s+/, '') });
      continue;
    }

    const isReadableOutcome =
      line.includes('완료') ||
      line.includes('업로드') ||
      line.includes('Uploaded') ||
      line.includes('Created') ||
      line.includes('View in AgentTeams') ||
      line.includes('계획 본문') ||
      /^https:\/\/agentteams\.run\//.test(line);

    if (isReadableOutcome && !isHiddenCodexLine(line)) {
      items.push({ level: 'INFO', message: line });
    }
  }

  return items;
};

const CODEX_STRUCTURED_LOG_PREFIXES = ['[Tool]', '[Result]', '[Session]', '[Thinking]', '[Patch]'] as const;

export const formatCodexReadableLogs = (logs: DaemonTriggerLog[]): DaemonTriggerLog[] => {
  const hasStructuredLog = logs.some((log) => {
    const message = log.message.trim();
    return message.length > 0 && CODEX_STRUCTURED_LOG_PREFIXES.some((prefix) => message.startsWith(prefix));
  });
  if (hasStructuredLog) return logs;

  const readableLogs = logs.flatMap((log) =>
    toCodexReadableMessage(log.message).map((item, index) => ({
      ...log,
      id: `${log.id}:codex-readable:${index}`,
      level: item.level,
      message: item.message,
    })),
  );

  return readableLogs.length > 0 ? readableLogs : logs;
};

export const getRunnerTypeLabel = (runnerType: string, tLayout: (key: string) => string): string => {
  if (runnerType === 'CLAUDE_CODE') return tLayout('runnerTypeClaudeCode');
  if (runnerType === 'OPENCODE') return tLayout('runnerTypeOpencode');
  if (runnerType === 'CODEX') return tLayout('runnerTypeCodex');
  if (runnerType === 'GEMINI') return tLayout('runnerTypeGemini');
  if (runnerType === 'ANTIGRAVITY') return tLayout('runnerTypeAntigravity');
  if (runnerType === 'AMP') return tLayout('runnerTypeAmp');
  if (runnerType === 'COPILOT_CLI') return tLayout('runnerTypeCopilotCli');
  if (runnerType === 'CURSOR_CLI') return tLayout('runnerTypeCursorCli');
  if (runnerType === 'KIMI_CLI') return tLayout('runnerTypeKimiCli');
  if (runnerType === 'KIRO_CLI') return tLayout('runnerTypeKiroCli');
  if (runnerType === 'GROK_BUILD') return tLayout('runnerTypeGrokBuild');
  if (runnerType === 'MUSE_CODE') return tLayout('runnerTypeMuseCode');
  if (runnerType === 'OMP') return tLayout('runnerTypeOmp');
  return runnerType;
};

export const getRunnerTypeIcon = (runnerType: string): { src: string; mono: boolean } => {
  switch (runnerType) {
    case 'CLAUDE_CODE':
      return { src: '/agents/claude.svg', mono: false };
    case 'CODEX':
      return { src: '/agents/openai.svg', mono: true };
    case 'GEMINI':
      return { src: '/agents/gemini.svg', mono: false };
    case 'ANTIGRAVITY':
      return { src: '/agents/antigravity.svg', mono: false };
    case 'OPENCODE':
      return { src: '/agents/opencode.svg', mono: true };
    case 'AMP':
      return { src: '/agents/ampcode.svg', mono: true };
    case 'COPILOT_CLI':
      return { src: '/agents/copilot.svg', mono: true };
    case 'CURSOR_CLI':
      return { src: '/agents/cursor.svg', mono: false };
    case 'KIMI_CLI':
      return { src: '/agents/kimi.svg', mono: false };
    case 'KIRO_CLI':
      return { src: '/agents/kiro.svg', mono: false };
    case 'GROK_BUILD':
      return { src: '/agents/grok.svg', mono: true };
    case 'MUSE_CODE':
      return { src: '/agents/muse.svg', mono: true };
    case 'OMP':
      return { src: '/agents/omp.svg', mono: true };
    default:
      return { src: '/agents/claude.svg', mono: false };
  }
};
